import { i18n } from "../../../boot/i18n";
import { store } from "../../../store";

// Children of the "warehouse" parent, so their paths are relative -- the URLs
// they resolve to (/warehouse/receiving, /warehouse/qc-scan, ...) are unchanged
// from when these were flat siblings.
const children = [
  {
    name: "inventory",
    // `query` only means anything on the Search filter.
    path: "inventory/:filter?/:query?",
    meta: {
      icon: "window",
      title: i18n.global.t("Inventory"),
      crumbs: ["warehouse-tools"],
    },
    // The "all" view was renamed to "holding" — it never included
    // delivered/installed items, which live in "archive". Keep old links and
    // bookmarks working.
    beforeEnter: (to) =>
      to.params.filter === "all"
        ? { name: "inventory", params: { filter: "holding" }, replace: true }
        : true,
    component: () => import("pages/employee/Inventory"),
  },
  {
    name: "inventory-counts",
    path: "inventory-counts",
    meta: {
      auth: () => store.getters.isEmployee(),
      icon: "inventory_count",
      title: i18n.global.t("Inventory Counts"),
      crumbs: ["warehouse-tools"],
    },
    component: () => import("pages/employee/InventoryCounts"),
  },
  {
    name: "inventory-count",
    path: "inventory-counts/:countID",
    meta: {
      auth: () => store.getters.isEmployee(),
      icon: "inventory_count",
      title: i18n.global.t("Inventory Count"),
      crumbs: ["warehouse-tools"],
      hideFromIndex: true,
    },
    component: () => import("pages/employee/InventoryCount"),
  },
  {
    name: "warehouse-receiving",
    path: "receiving",
    meta: {
      icon: "receiving",
      title: i18n.global.t("Receiving"),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
    },
    component: () => import("pages/employee/Receiving"),
  },
  {
    name: "warehouse-receiving-session",
    path: "receiving/:sessionID",
    props: true,
    meta: {
      icon: "receiving",
      title: i18n.global.t("Receiving"),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
      hideFromIndex: true,
    },
    component: () => import("pages/employee/Receiving"),
  },
  {
    name: "warehouse-receiving-complete",
    path: "receiving/:sessionID/complete",
    props: true,
    meta: {
      icon: "receiving",
      title: i18n.global.t("Complete Receiving"),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
      hideFromIndex: true,
    },
    component: () => import("pages/employee/Receiving"),
  },
  {
    name: "warehouse-qc-scan",
    path: "qc-scan",
    meta: {
      icon: "quality_control",
      title: i18n.global.t("Quality Control"),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
    },
    component: () => import("pages/employee/QCScan"),
  },
  {
    name: "inventory-transfers",
    path: "transfers",
    meta: {
      icon: "relocate",
      title: i18n.global.tc("Transfer", 100),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
    },
    component: () => import("pages/employee/InventoryTransfers"),
  },
  {
    name: "warehouse-unassigned",
    path: "unassigned-material",
    meta: {
      icon: "unassigned",
      title: i18n.global.t("warehouse.unassignedMaterial"),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
    },
    component: () => import("pages/employee/UnassignedMaterial"),
  },
  // Acknowledgements that are not for a sale (src/classes/OtherAck.js). One
  // page, a route per filter.
  {
    name: "warehouse-other-acks",
    path: "other-acknowledgements",
    meta: {
      icon: "ack",
      title: i18n.global.t("Other Acknowledgements"),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
    },
    component: () => import("pages/employee/OtherAcks"),
  },
  {
    name: "warehouse-other-acks-received",
    path: "other-acknowledgements/received",
    meta: {
      icon: "ack",
      title: i18n.global.t("Other Acknowledgements"),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
      hideFromIndex: true,
    },
    component: () => import("pages/employee/OtherAcks"),
  },
  {
    name: "warehouse-other-acks-search",
    path: "other-acknowledgements/search/:query?",
    meta: {
      icon: "ack",
      title: i18n.global.t("Other Acknowledgements"),
      auth: () => store.getters.isEmployee(),
      crumbs: ["warehouse-tools"],
      hideFromIndex: true,
    },
    component: () => import("pages/employee/OtherAcks"),
  },
];

export default {
  routes: [
    {
      name: "warehouse-tools",
      path: "warehouse",
      component: () => import("pages/warehouse/Index"),
      meta: {
        auth: () =>
          children.some((route) => route.meta.auth && route.meta.auth()),
        icon: "warehouse",
        title: i18n.global.t("warehouse.warehouseTools"),
        color: "primary",
      },
      children,
    },
  ],

  dialogs: [
    // Inventory
    {
      name: "inventory-count-new",
      path: "/inventory-counts/new",
      meta: {
        auth: () => store.getters.isEmployee(),
        icon: "inventory_count",
        title: i18n.global.t("inventoryCount.newCount"),
      },
      component: () => import("../../../dialogs/employee/InventoryCountNew"),
    },
    {
      name: "inventory-item-view",
      path: "/inventory/:itemID/view/:imageIndex/:zone",
      meta: {
        icon: "warehouse",
        title: i18n.global.tc("Inventory Item"),
      },
      component: () => import("../../../dialogs/employee/InventoryItemView"),
    },
    {
      name: "inventory-item-new",
      path: "/inventory/new",
      meta: {
        icon: "edit",
        title: i18n.global.t("New Inventory Item"),
      },
      component: () => import("../../../dialogs/employee/InventoryItemEdit"),
    },
    {
      name: "inventory-item-edit",
      path: "/inventory/:itemID/edit",
      meta: {
        icon: "edit",
        title: i18n.global.t("Edit Inventory Item"),
      },
      component: () => import("../../../dialogs/employee/InventoryItemEdit"),
    },
    {
      name: "inventory-item-relocate",
      path: "/inventory/:itemID/relocate",
      component: () => import("../../../dialogs/employee/RelocateItem"),
    },

    // Packing Slip Import
    {
      name: "packing-slip-import",
      path: "/projects/:projectID/sales/:saleID/vendor/:vendor/orders/:orderID/packing-slips/import",
      meta: { auth: () => store.getters.isEmployee() },
      component: () => import("../../../dialogs/employee/PackingSlipImport"),
    },

    // Receiving Mode (per-packing-slip)
    {
      name: "receiving-mode",
      path: "/projects/:projectID/sales/:saleID/vendor/:vendor/orders/:orderID/packing-slips/:packingSlipID/receive",
      meta: { auth: () => store.getters.isEmployee() },
      component: () => import("../../../dialogs/employee/ReceivingMode"),
    },

    // Vendor Receiving Mode
    {
      name: "vendor-receiving-mode",
      path: "/projects/:projectID/sales/:saleID/vendor/:vendor/orders/:orderID/vendor-receive",
      meta: { auth: () => store.getters.isEmployee() },
      component: () => import("../../../dialogs/employee/VendorReceivingMode"),
    },
    // ...and from a packing slip, which its Complete Receiving writes to. A
    // dialog push keeps only the params its path names, so the slip has to be
    // in the path to reach the dialog at all.
    {
      name: "packing-slip-vendor-receiving-mode",
      path: "/projects/:projectID/sales/:saleID/vendor/:vendor/orders/:orderID/packing-slips/:packingSlipID/vendor-receive",
      meta: { auth: () => store.getters.isEmployee() },
      component: () => import("../../../dialogs/employee/VendorReceivingMode"),
    },

    // Acknowledgements that are not for a sale. `new` takes what it is for,
    // and the service issue, when opened from one.
    {
      name: "other-ack-new",
      path: "/other-acknowledgements/new/:type/:serviceIssueID",
      meta: {
        auth: () => store.getters.isEmployee(),
        icon: "ack",
        title: i18n.global.t("New Acknowledgement"),
      },
      component: () => import("../../../dialogs/employee/OtherAckEdit"),
    },
    {
      name: "other-ack-edit",
      path: "/other-acknowledgements/:ackID/edit",
      meta: {
        auth: () => store.getters.isEmployee(),
        icon: "ack",
        title: i18n.global.t("Edit Acknowledgement"),
      },
      component: () => import("../../../dialogs/employee/OtherAckEdit"),
    },
    {
      name: "other-ack-view",
      path: "/other-acknowledgements/:ackID/view",
      meta: {
        auth: () => store.getters.isEmployee(),
        icon: "ack",
        title: i18n.global.t("Acknowledgement"),
      },
      component: () => import("../../../dialogs/employee/OtherAckView"),
    },
  ],
};
